'use client'

import { useEffect, useRef, useState } from 'react'

interface FaqItem {
  question: string
  answer: string
}

/**
 * /services/ai-product-development -- the body, ported from
 * `public/radar-branding/ai-product-development.html`. See
 * `ai-product-development.css` for what the port changed and why.
 *
 * Client only for the mock's two real behaviours:
 *
 *   1. Reveal on scroll. Content renders VISIBLE by default and is armed only
 *      once this mounts (`is-enhanced`), so SSR, a failed hydration and
 *      `prefers-reduced-motion` all show the finished page.
 *   2. The FAQ show-more, which the mock drove by toggling a `revealed` class
 *      and rewriting the button label. Both are state here.
 *
 * The mock's scroll-progress bar is dropped: the root layout renders
 * `ScrollProgress`.
 */
/**
 * `heroPrice` comes from `getServicePriceModel()` in the route -- the same call
 * that builds the JSON-LD Offer. This string used to be hardcoded here, which
 * is exactly the drift that helper exists to prevent: the visible price and the
 * structured-data price have to move together.
 */
export default function AiProductDevelopmentContent({
  faqItems,
  heroPrice,
}: {
  faqItems: readonly FaqItem[]
  heroPrice?: string | null
}) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [enhanced, setEnhanced] = useState(false)
  const [faqOpen, setFaqOpen] = useState(false)

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const targets = Array.from(root.querySelectorAll('.apd-reveal'))
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      targets.forEach(el => el.classList.add('apd-visible'))
      return
    }
    setEnhanced(true)
    const io = new IntersectionObserver(
      entries =>
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('apd-visible')
            io.unobserve(e.target)
          }
        }),
      { threshold: 0.08 }
    )
    targets.forEach(el => io.observe(el))
    return () => io.disconnect()
  }, [])

  return (
    <div
      ref={rootRef}
      className={`apd-page${enhanced ? ' apd-is-enhanced' : ''}`}
    >
      <section className='apd-hero' id='top'>
        <div className='apd-wrap'>
          <div className='apd-hero-grid'>
            <div className='apd-hero-copy apd-reveal apd-visible'>
              <p className='apd-label apd-hero-eyebrow'>
                Best for funded startups ($1M+) shipping AI before next round
              </p>
              <h1 className='apd-display'>
                We build AI products. You ship to{' '}
                <span className='apd-pink'>paying users in 90 days.</span>
              </h1>
              <p className='apd-hero-body'>
                We take your validated idea from architecture to production with
                AI integrated. Design, code, deployment, the whole thing. Not
                for unvalidated ideas (talk to customers first), budgets under
                $15K, or teams that want staff augmentation.
              </p>
              <p className='apd-hero-support'>
                Full product development from validation to launch. Also covers
                adding AI to existing products and interface optimization.
              </p>
              <div className='apd-hero-actions'>
                <a className='apd-btn apd-btn-primary' href='/contact-us'>
                  Start your project <span className='apd-arrow'>→</span>
                </a>
                <a
                  className='apd-btn apd-btn-secondary'
                  href='/services/revenue-first-design'
                >
                  Explore design systems
                </a>
              </div>
              {heroPrice ? (
                <p className='apd-label apd-hero-price'>{heroPrice}</p>
              ) : null}
            </div>

            <aside
              className='apd-assembly-console apd-reveal apd-visible'
              aria-label='Product assembly animation'
            >
              <div className='apd-assembly-head'>
                <p className='apd-label'>Build manifest · 4 layers</p>
                <span className='apd-label'>Assembling</span>
              </div>
              <div className='apd-assembly-frame'>
                <svg
                  viewBox='0 0 240 240'
                  className='apd-assembly-svg'
                  role='img'
                  aria-label='Animated diagram of frontend, backend, AI, and data modules assembling into a finished product'
                >
                  <rect
                    x='28'
                    y='32'
                    width='184'
                    height='176'
                    fill='none'
                    style={{ stroke: 'var(--d-diagram-ring)' }}
                    strokeWidth='1'
                  />
                  <g
                    className='apd-pm-build-rise'
                    style={{ animationDelay: '.6s' }}
                  >
                    <rect
                      x='40'
                      y='48'
                      width='160'
                      height='30'
                      style={{ fill: 'var(--d-blue)', stroke: 'var(--d-blue)' }}
                      fillOpacity='.12'
                      strokeOpacity='.5'
                      strokeWidth='1'
                    />
                    <rect
                      x='48'
                      y='56'
                      width='14'
                      height='14'
                      style={{ fill: 'var(--d-blue)' }}
                    />
                    <rect
                      x='70'
                      y='57'
                      width='90'
                      height='4'
                      style={{ fill: 'var(--d-diagram-ring)' }}
                    />
                    <rect
                      x='70'
                      y='66'
                      width='60'
                      height='4'
                      style={{ fill: 'var(--d-diagram-ring)' }}
                    />
                  </g>
                  <g
                    className='apd-pm-build-rise'
                    style={{ animationDelay: '.4s' }}
                  >
                    <rect
                      x='40'
                      y='86'
                      width='160'
                      height='30'
                      style={{
                        fill: 'var(--d-purple)',
                        stroke: 'var(--d-purple)',
                      }}
                      fillOpacity='.12'
                      strokeOpacity='.5'
                      strokeWidth='1'
                    />
                    <rect
                      x='48'
                      y='94'
                      width='14'
                      height='14'
                      style={{ fill: 'var(--d-purple)' }}
                    />
                    <rect
                      x='70'
                      y='95'
                      width='90'
                      height='4'
                      style={{ fill: 'var(--d-diagram-ring)' }}
                    />
                    <rect
                      x='70'
                      y='104'
                      width='60'
                      height='4'
                      style={{ fill: 'var(--d-diagram-ring)' }}
                    />
                  </g>
                  <g
                    className='apd-pm-build-rise'
                    style={{ animationDelay: '.2s' }}
                  >
                    <rect
                      x='40'
                      y='124'
                      width='160'
                      height='30'
                      style={{
                        fill: 'var(--d-yellow)',
                        stroke: 'var(--d-yellow)',
                      }}
                      fillOpacity='.12'
                      strokeOpacity='.5'
                      strokeWidth='1'
                    />
                    <rect
                      x='48'
                      y='132'
                      width='14'
                      height='14'
                      style={{ fill: 'var(--d-yellow)' }}
                    />
                    <rect
                      x='70'
                      y='133'
                      width='90'
                      height='4'
                      style={{ fill: 'var(--d-diagram-ring)' }}
                    />
                    <rect
                      x='70'
                      y='142'
                      width='60'
                      height='4'
                      style={{ fill: 'var(--d-diagram-ring)' }}
                    />
                  </g>
                  <g
                    className='apd-pm-build-rise'
                    style={{ animationDelay: '0s' }}
                  >
                    <rect
                      x='40'
                      y='162'
                      width='160'
                      height='30'
                      style={{
                        fill: 'var(--d-green)',
                        stroke: 'var(--d-green)',
                      }}
                      fillOpacity='.12'
                      strokeOpacity='.5'
                      strokeWidth='1'
                    />
                    <rect
                      x='48'
                      y='170'
                      width='14'
                      height='14'
                      style={{ fill: 'var(--d-green)' }}
                    />
                    <rect
                      x='70'
                      y='171'
                      width='90'
                      height='4'
                      style={{ fill: 'var(--d-diagram-ring)' }}
                    />
                    <rect
                      x='70'
                      y='180'
                      width='60'
                      height='4'
                      style={{ fill: 'var(--d-diagram-ring)' }}
                    />
                  </g>
                </svg>
              </div>
              <p className='apd-assembly-caption'>
                Validate, design, build, and launch, assembled into a product
                you own.
              </p>
              <div className='apd-assembly-legend' aria-label='Product layers'>
                <span>
                  <i></i>Frontend
                </span>
                <span>
                  <i></i>Backend
                </span>
                <span>
                  <i></i>AI
                </span>
                <span>
                  <i></i>Data
                </span>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className='apd-context-bar'>
        <div className='apd-wrap'>
          <p className='apd-hero-context apd-reveal'>
            Most AI products fail not because of bad models but because of poor
            integration between the AI layer and the product experience. We
            handle the whole thing: validation, model selection, data pipelines,
            interfaces, deployment. You get a working product with real users,
            not a demo that never ships. This also covers adding AI to existing
            products and optimizing interfaces for conversion.
          </p>
          <div className='apd-stats apd-reveal'>
            <article className='apd-stat'>
              <strong>90 days</strong>
              <p className='apd-label'>Launch window</p>
              <p>Idea to paying users with revenue telemetry wired in.</p>
            </article>
            <article className='apd-stat'>
              <strong>Weeks</strong>
              <p className='apd-label'>To validation</p>
              <p>
                Dual-LLM research loops compress discovery to weeks, not months,
                before any code is written.
              </p>
            </article>
            <article className='apd-stat'>
              <strong>0</strong>
              <p className='apd-label'>Critical vulnerabilities</p>
              <p>
                Thread-Based Engineering scans for security issues at generation
                time under CLAUDE.md governance, so they are caught before code
                exists, not at deploy.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className='apd-stall' id='stall'>
        <div className='apd-wrap apd-stall-grid'>
          <div className='apd-stall-copy apd-reveal'>
            <p className='apd-label apd-label-pink'>
              Why most AI products stall
            </p>
            <h2 className='apd-display'>
              The model is rarely the problem. Integration is.
            </h2>
            <p>We ship the whole system, not the demo.</p>
          </div>
          <div className='apd-stall-board apd-reveal'>
            <div className='apd-stall-lanes'>
              <article className='apd-stall-lane'>
                <div>
                  <p className='apd-label'>A demo</p>
                  <h3>A model in a notebook</h3>
                </div>
                <div className='apd-stall-list'>
                  <div className='apd-stall-item'>
                    <i>01</i>Works only in the happy path
                  </div>
                  <div className='apd-stall-item'>
                    <i>02</i>No auth, data, or telemetry
                  </div>
                </div>
              </article>
              <article className='apd-stall-lane'>
                <div>
                  <p className='apd-label apd-label-cyan'>A shipped product</p>
                  <h3>Model wired into the product</h3>
                </div>
                <div className='apd-stall-list'>
                  <div className='apd-stall-item'>
                    <i>01</i>Auth, data pipelines, observability
                  </div>
                  <div className='apd-stall-item'>
                    <i>02</i>Revenue telemetry on day one
                  </div>
                </div>
              </article>
            </div>
            <div className='apd-stall-gap'>
              <strong>The gap:</strong> a model that works in isolation is not a
              product. We build the integration, data, and telemetry that turn
              it into something real users pay for.
            </div>
          </div>
        </div>
      </section>

      <section className='apd-pricing' id='packages'>
        <div className='apd-wrap'>
          <div className='apd-pricing-head apd-reveal'>
            <div>
              <p className='apd-label apd-label-pink'>Commercial model</p>
              <h2 className='apd-display'>Pricing & Packages</h2>
            </div>
            <p>
              Choose the package that fits your needs. All packages include
              post-launch support.
            </p>
          </div>
          <div className='apd-package-grid apd-reveal'>
            <article className='apd-package'>
              <div className='apd-package-head'>
                <span className='apd-package-index'>01 / Starter</span>
                <h3>Starter</h3>
                <p className='apd-package-price'>$4,995-$7,995</p>
                <p className='apd-package-desc'>
                  Testing ideas, early-stage validation
                </p>
              </div>
              <ul className='apd-package-list'>
                <li>User research and validation (5-10 interviews)</li>
                <li>Wireframes and user flows</li>
                <li>Interactive prototype (clickable)</li>
                <li>Landing page for early signups</li>
                <li>Basic analytics setup</li>
                <li>Technical architecture recommendation</li>
                <li>Product roadmap (next 6 months)</li>
                <li>Success metrics dashboard</li>
              </ul>
              <div className='apd-package-time'>
                <span>Estimated Timeline:</span>
                <strong>3-4 weeks</strong>
              </div>
            </article>
            <article className='apd-package'>
              <div className='apd-package-head'>
                <span className='apd-package-index'>02 / Growth</span>
                <span className='apd-popular'>Most Popular</span>
                <h3>Growth</h3>
                <p className='apd-package-price'>$12,995-$19,995</p>
                <p className='apd-package-value'>
                  Only $8K more but includes complete development + AI
                  integration + 2 months support
                </p>
                <p className='apd-package-desc'>
                  Production-ready products and SaaS platforms
                </p>
              </div>
              <ul className='apd-package-list'>
                <li>Everything in Starter, plus:</li>
                <li>Complete product design (20-30 screens)</li>
                <li>AI integration architecture</li>
                <li>Full-stack development (frontend + backend)</li>
                <li>User authentication and authorization</li>
                <li>Database design and setup</li>
                <li>AI model integration (OpenAI, Anthropic)</li>
                <li>Admin dashboard</li>
                <li>Analytics and telemetry</li>
                <li>Deployment and hosting setup</li>
                <li>Documentation and handoff</li>
                <li>2 months post-launch support</li>
              </ul>
              <div className='apd-package-time'>
                <span>Estimated Timeline:</span>
                <strong>8-12 weeks</strong>
              </div>
            </article>
            <article className='apd-package'>
              <div className='apd-package-head'>
                <span className='apd-package-index'>03 / Scale</span>
                <h3>Scale</h3>
                <p className='apd-package-price'>$29,995-$49,995</p>
                <p className='apd-package-desc'>
                  Complex AI systems and enterprise platforms
                </p>
              </div>
              <ul className='apd-package-list'>
                <li>Everything in Growth, plus:</li>
                <li>Complete platform architecture (40+ screens)</li>
                <li>Multi-tenant infrastructure</li>
                <li>Advanced AI capabilities (RAG, agents)</li>
                <li>Enterprise security and compliance</li>
                <li>Role-based access control</li>
                <li>API development and documentation</li>
                <li>Admin + client portals</li>
                <li>Advanced analytics</li>
                <li>Integration with enterprise tools</li>
                <li>Scalable infrastructure</li>
                <li>Comprehensive documentation</li>
                <li>Team training</li>
                <li>6 months post-launch support</li>
              </ul>
              <div className='apd-package-time'>
                <span>Estimated Timeline:</span>
                <strong>12-16 weeks</strong>
              </div>
            </article>
          </div>

          <div className='apd-compare apd-reveal'>
            <div className='apd-compare-intro'>
              <p className='apd-label'>Agency comparison</p>
              <h3>Why Pixelmojo?</h3>
              <p>Compare traditional agencies vs our AI-powered approach</p>
            </div>
            <div className='apd-compare-table'>
              <div className='apd-compare-col'>
                <div className='apd-compare-title'>Traditional Agency</div>
                <div className='apd-compare-row'>6-12 month timelines</div>
                <div className='apd-compare-row'>$50K-$200K+ budgets</div>
                <div className='apd-compare-row'>Fixed deliverables</div>
                <div className='apd-compare-row'>Limited revisions</div>
                <div className='apd-compare-row'>Handoff and done</div>
                <div className='apd-compare-row'>No ongoing optimization</div>
              </div>
              <div className='apd-compare-col'>
                <div className='apd-compare-title'>Pixelmojo (AI-Powered)</div>
                <div className='apd-compare-row'>4-12 week delivery</div>
                <div className='apd-compare-row'>$1,995-$49,995 budgets</div>
                <div className='apd-compare-row'>Flexible and iterative</div>
                <div className='apd-compare-row'>Collaborative process</div>
                <div className='apd-compare-row'>
                  Post-launch support included
                </div>
                <div className='apd-compare-row'>Built for optimization</div>
              </div>
            </div>
          </div>

          <div className='apd-call-band apd-reveal'>
            <div className='apd-call-main'>
              <div>
                <p className='apd-label apd-label-pink'>
                  Free 30-Min Strategy Call
                </p>
                <h3>Let's talk about your project</h3>
                <p>
                  We'll review your needs, discuss options, and create a custom
                  proposal. No pressure, no obligation.
                </p>
              </div>
              <a className='apd-btn apd-btn-primary' href='/contact-us'>
                Book your free call <span className='apd-arrow'>→</span>
              </a>
            </div>
            <aside className='apd-call-local'>
              <h4>🇵🇭 Philippines Startup Special</h4>
              <p className='apd-discount'>15-20% off</p>
              <p>
                We're based in the Philippines and want to support local
                startups. Book a call to see if you qualify.
              </p>
            </aside>
          </div>
        </div>
      </section>

      <section className='apd-ways'>
        <div className='apd-wrap'>
          <div className='apd-ways-head apd-reveal'>
            <p className='apd-label apd-label-pink'>Build entry points</p>
            <h2 className='apd-display'>Three ways to start</h2>
            <p>
              Validate an idea, ship a production product, or build an
              enterprise platform. You own the code at every tier.
            </p>
          </div>
          <div className='apd-ways-grid apd-reveal'>
            <article className='apd-way'>
              <div>
                <p className='apd-label apd-label-cyan'>MVP Validation</p>
                <h3>MVP Validation</h3>
                <p>Testing ideas and early-stage validation</p>
              </div>
              <div className='apd-way-meta'>
                <span>$4,995 to $7,995</span>
                <span>3 to 4 weeks</span>
              </div>
            </article>
            <article className='apd-way'>
              <div>
                <p className='apd-label apd-label-pink'>Most popular</p>
                <h3>Full AI Product</h3>
                <p>Production-ready products and SaaS platforms</p>
              </div>
              <div className='apd-way-meta'>
                <span>$12,995 to $19,995</span>
                <span>8 to 12 weeks</span>
              </div>
            </article>
            <article className='apd-way'>
              <div>
                <p className='apd-label'>Enterprise AI Product</p>
                <h3>Enterprise AI Product</h3>
                <p>Complex AI systems and enterprise platforms</p>
              </div>
              <div className='apd-way-meta'>
                <span>$29,995 to $49,995</span>
                <span>12 to 16 weeks</span>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className='apd-validation'>
        <div className='apd-wrap apd-validation-grid'>
          <div className='apd-validation-copy apd-reveal'>
            <p className='apd-label'>Validation without the drag</p>
            <h2 className='apd-display'>Proof before code hits main</h2>
            <p>
              Discovery and building happen in parallel. Every prototype gets in
              front of real users, and we measure against the revenue targets
              your board already cares about.
            </p>
            <div className='apd-validation-list'>
              <div className='apd-validation-item'>
                <i>01</i>
                <span>
                  Thread-Based Engineering runs parallel AI workflows with
                  governance checkpoints that catch 'almost right' AI output
                  before it reaches your codebase.
                </span>
              </div>
              <div className='apd-validation-item'>
                <i>02</i>
                <span>
                  Weekly telemetry syncs translate live customer behaviour into
                  sprint-ready stories.
                </span>
              </div>
              <div className='apd-validation-item'>
                <i>03</i>
                <span>
                  Revenue instrumentation ships inside prototypes so leadership
                  sees ARR impact instantly.
                </span>
              </div>
            </div>
          </div>
          <div className='apd-loop-board apd-reveal'>
            <h3>Proof before code hits main</h3>
            <div className='apd-loop-steps'>
              <div className='apd-loop-step'>
                <i>01</i>
                <strong>Prototype with AI copilots</strong>
              </div>
              <div className='apd-loop-step'>
                <i>02</i>
                <strong>Test with real users</strong>
              </div>
              <div className='apd-loop-step'>
                <i>03</i>
                <strong>Measure against revenue targets</strong>
              </div>
              <div className='apd-loop-step'>
                <i>04</i>
                <strong>Feed results into the backlog</strong>
              </div>
            </div>
            <p className='apd-loop-caption'>
              Discovery and build run in parallel, so decisions rest on real
              behaviour, not opinion.
            </p>
          </div>
        </div>
      </section>

      <section className='apd-systems' id='systems'>
        <div className='apd-wrap'>
          <div className='apd-systems-head apd-reveal'>
            <h2 className='apd-display'>Systems we build in the first month</h2>
            <p>
              Your product leaves the lab with the infrastructure, design
              language, and growth telemetry required to scale, not just a shiny
              prototype.
            </p>
          </div>
          <div className='apd-system-rows apd-reveal'>
            <article className='apd-system-row'>
              <div className='apd-system-name'>
                <span>Foundation</span>
                <h3>AI core ready for production</h3>
              </div>
              <div className='apd-system-items'>
                <div className='apd-system-item'>
                  Model orchestration across Claude, GPT-4o, and Bedrock with
                  guardrails in place.
                </div>
                <div className='apd-system-item'>
                  CLAUDE.md governance and automated security scanning at
                  generation time, not deployment time, preventing
                  vulnerabilities before code exists.
                </div>
                <div className='apd-system-item'>
                  Latency budgets and cost envelopes set before engineering
                  sprints begin.
                </div>
              </div>
            </article>
            <article className='apd-system-row'>
              <div className='apd-system-name'>
                <span>Experience Layer</span>
                <h3>Interfaces tuned for monetization</h3>
              </div>
              <div className='apd-system-items'>
                <div className='apd-system-item'>
                  Activation flows driven by battle-tested design system tokens.
                </div>
                <div className='apd-system-item'>
                  Personalization surfaces mapped to the segments that drive
                  ARR.
                </div>
                <div className='apd-system-item'>
                  Cross-team service blueprints so success, sales, and product
                  see the same story.
                </div>
              </div>
            </article>
            <article className='apd-system-row'>
              <div className='apd-system-name'>
                <span>Growth & Ops</span>
                <h3>Telemetry that proves ROI</h3>
              </div>
              <div className='apd-system-items'>
                <div className='apd-system-item'>
                  Revenue dashboards in Mixpanel/dbt ready before beta users
                  arrive.
                </div>
                <div className='apd-system-item'>
                  Anomaly alerts piped into Slack to prevent silent churn
                  moments.
                </div>
                <div className='apd-system-item'>
                  Feedback automations pushing customer evidence into Jira and
                  Notion instantly.
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className='apd-inside'>
        <div className='apd-wrap'>
          <div className='apd-inside-head apd-reveal'>
            <div>
              <p className='apd-label apd-label-pink'>
                What is inside your AI product
              </p>
              <h2 className='apd-display'>
                A full stack, not a model in isolation.
              </h2>
            </div>
            <p>Every layer ships to you.</p>
          </div>
          <div className='apd-product-stack apd-reveal'>
            <div className='apd-product-layer'>
              <i></i>
              <strong>Frontend and UI</strong>
              <span>
                Activation flows built on a battle-tested design system
              </span>
            </div>
            <div className='apd-product-layer'>
              <i></i>
              <strong>Backend and API</strong>
              <span>Full-stack application logic and service endpoints</span>
            </div>
            <div className='apd-product-layer'>
              <i></i>
              <strong>AI model integration</strong>
              <span>OpenAI and Anthropic models with guardrails in place</span>
            </div>
            <div className='apd-product-layer'>
              <i></i>
              <strong>Auth and data</strong>
              <span>
                User authentication, authorization, and database design
              </span>
            </div>
            <div className='apd-product-layer'>
              <i></i>
              <strong>Analytics and telemetry</strong>
              <span>Revenue dashboards and anomaly alerts wired in early</span>
            </div>
            <div className='apd-inside-note'>
              You own all of it: code, UI, integrations, and infrastructure.
              Enterprise builds add RAG, multi-agent orchestration, RBAC, and
              multi-tenant infrastructure.
            </div>
          </div>
        </div>
      </section>

      <section className='apd-delivery'>
        <div className='apd-wrap'>
          <div className='apd-timeline-head apd-reveal'>
            <div>
              <p className='apd-label'>Delivery sequence</p>
              <h2 className='apd-display'>Validation to launch, in weeks</h2>
            </div>
            <p>
              Each phase lands tangible assets, approvals, and learnings your
              team can reuse.
            </p>
          </div>
          <div className='apd-timeline apd-reveal'>
            <article className='apd-timeline-step'>
              <div>
                <span>Weeks 1-2</span>
                <h3>Signals sprint</h3>
                <p>Mine telemetry and goals into a revenue-ranked backlog.</p>
              </div>
            </article>
            <article className='apd-timeline-step'>
              <div>
                <span>Weeks 3-4</span>
                <h3>Validation lab</h3>
                <p>Prototypes meet real users; every test loops data back.</p>
              </div>
            </article>
            <article className='apd-timeline-step'>
              <div>
                <span>Weeks 5-9</span>
                <h3>Build and integrate</h3>
                <p>Engineering, data, and design ship the production slice.</p>
              </div>
            </article>
            <article className='apd-timeline-step'>
              <div>
                <span>Weeks 10-12</span>
                <h3>Revenue launch</h3>
                <p>Pilot cohorts activate with dashboards live on day one.</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className='apd-team' id='ownership'>
        <div className='apd-wrap apd-team-grid'>
          <div className='apd-team-copy apd-reveal'>
            <p className='apd-label apd-label-pink'>Embedded team</p>
            <h2 className='apd-display'>
              Pods that drop into your roadmap without friction
            </h2>
            <p>
              We work inside your tools, attend your standups, and leave you
              with documentation so your team can maintain everything after we
              roll off.
            </p>
            <div className='apd-pod-list'>
              <div className='apd-pod'>
                <i>01</i>
                <span>
                  Product strategy lead aligns exec expectations and keeps
                  decisions moving.
                </span>
              </div>
              <div className='apd-pod'>
                <i>02</i>
                <span>
                  AI enablement engineer pairs with your developers to harden
                  integrations and data.
                </span>
              </div>
              <div className='apd-pod'>
                <i>03</i>
                <span>
                  Growth analyst converts real usage signals into backlog
                  priorities every single week.
                </span>
              </div>
            </div>
          </div>
          <div className='apd-ownership apd-reveal'>
            <div className='apd-ownership-vendor'>
              <p className='apd-label'>Typical AI vendor</p>
              <strong>You rent the product and stay locked in</strong>
            </div>
            <div className='apd-ownership-own'>
              <p className='apd-label apd-label-pink'>
                With Pixelmojo, you own
              </p>
              <div className='apd-own-list'>
                <div className='apd-own-row'>Application source code</div>
                <div className='apd-own-row'>UI and design system</div>
                <div className='apd-own-row'>
                  Integrations and infrastructure
                </div>
              </div>
            </div>
            <div className='apd-ownership-note'>
              No vendor lock-in. We maintain only the intelligence platform.
            </div>
          </div>
        </div>
      </section>

      <section className='apd-deliverables'>
        <div className='apd-wrap'>
          <div className='apd-deliverables-head apd-reveal'>
            <h2 className='apd-display'>What you walk away with</h2>
            <p>
              Documentation, assets, and growth plans that help internal teams
              keep scaling long after launch.
            </p>
          </div>
          <div className='apd-deliverable-grid apd-reveal'>
            <article className='apd-deliverable'>
              <div>
                <p className='apd-label apd-label-cyan'>Product</p>
                <h3>Launch-ready product stack</h3>
              </div>
              <ul>
                <li>
                  MVP shipped with auth, data pipelines, analytics, and
                  observability configured.
                </li>
                <li>
                  Operational runbooks so internal teams can iterate without
                  losing velocity.
                </li>
              </ul>
            </article>
            <article className='apd-deliverable'>
              <div>
                <p className='apd-label apd-label-pink'>Evidence</p>
                <h3>Evidence library</h3>
              </div>
              <ul>
                <li>
                  User research recordings, transcripts, and summary briefs tied
                  to roadmap decisions.
                </li>
                <li>
                  Experiment results visualised for board updates and investor
                  conversations.
                </li>
              </ul>
            </article>
            <article className='apd-deliverable'>
              <div>
                <p className='apd-label'>Roadmap</p>
                <h3>Scale roadmap</h3>
              </div>
              <ul>
                <li>
                  Prioritized backlog mapping next four quarters of feature
                  releases.
                </li>
                <li>
                  Hiring, automation, and partner recommendations aligned to
                  revenue targets.
                </li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section className='apd-faq' id='faq'>
        <div className='apd-wrap apd-faq-grid'>
          <div className='apd-faq-copy apd-reveal'>
            <p className='apd-label apd-label-pink'>Help center</p>
            <h2 className='apd-display'>Frequently Asked Questions</h2>
            <p>Everything you need to know about working with Pixelmojo.</p>
          </div>
          <div className='apd-faq-list apd-reveal'>
            {faqItems.slice(0, 4).map((item, index) => (
              <details key={item.question} open={index === 0}>
                <summary>{item.question}</summary>
                <div className='apd-answer'>{item.answer}</div>
              </details>
            ))}
            <div
              className={`apd-faq-more${faqOpen ? ' apd-revealed' : ''}`}
              id='faqMore'
            >
              {faqItems.slice(4).map(item => (
                <details key={item.question}>
                  <summary>{item.question}</summary>
                  <div className='apd-answer'>{item.answer}</div>
                </details>
              ))}
            </div>
            {faqItems.length > 4 ? (
              <button
                className='apd-show-more'
                id='showFaq'
                type='button'
                aria-controls='faqMore'
                aria-expanded={faqOpen}
                onClick={() => setFaqOpen(v => !v)}
              >
                {faqOpen
                  ? 'Show fewer ↑'
                  : `Show ${faqItems.length - 4} more ↓`}
              </button>
            ) : null}
          </div>
        </div>
      </section>

      <section className='apd-proof'>
        <div className='apd-wrap'>
          <div className='apd-proof-head apd-reveal'>
            <p className='apd-label'>Proof, not promises</p>
            <h2 className='apd-display'>
              We ship our own products the same way
            </h2>
            <p>
              No name-drops here by design. The proof is what we have built and
              what you own.
            </p>
          </div>
          <div className='apd-proof-grid apd-reveal'>
            <article className='apd-proof-card'>
              <div>
                <span className='apd-proof-number'>3 products</span>
                <p>
                  We built Vector, Hive, and Radar ourselves, shipping software,
                  not slideware.
                </p>
              </div>
              <a href='/platform'>
                See Radar <span className='apd-arrow'>↗</span>
              </a>
            </article>
            <article className='apd-proof-card'>
              <div>
                <span className='apd-proof-number'>You own it</span>
                <p>
                  All application code, UI, integrations, and infrastructure
                  ship to you. No vendor lock-in.
                </p>
              </div>
            </article>
            <article className='apd-proof-card'>
              <div>
                <span className='apd-proof-number'>Day one</span>
                <p>
                  Revenue telemetry is wired into every prototype, so leadership
                  sees impact from launch.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className='apd-final-cta'>
        <div className='apd-final-inner apd-reveal'>
          <h2 className='apd-display'>Let's see if this is the right fit.</h2>
          <p>
            30-minute call. We will ask about your product, your users, and your
            timeline. If we are not the right fit, we will tell you on the call.
          </p>
          <a className='apd-btn apd-btn-primary' href='/contact-us'>
            Book the roadmap sprint <span className='apd-arrow'>↗</span>
          </a>
        </div>
      </section>
    </div>
  )
}
